
import { useState } from 'react';
import { bundleDeals } from '../../../mocks/promoDeals';

export default function BundleDeals() {
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const handleClaim = async (bundleId: string) => {
    setLoadingId(bundleId);
    try {
      const response = await fetch(
        'https://puoyglsrhhxtslsqwexu.supabase.co/functions/v1/create-subscription',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planName: bundleId, isYearly: true }),
        }
      );
      const data = await response.json().catch(() => { throw new Error('Invalid response'); });
      if (data.success && data.url) {
        window.location.href = data.url;
      }
    } catch {
      // Fallback
    } finally {
      setLoadingId(null);
    }
  };

  const tagColors: Record<string, string> = {
    'Most Popular': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    'Best Value': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    'Ultimate': 'bg-rose-500/15 text-rose-400 border-rose-500/30',
  };

  return (
    <section id="bundles" className="py-24 bg-slate-900">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-gift-2-line text-emerald-400 w-4 h-4 flex items-center justify-center"></i>
            <span className="text-emerald-300 text-sm font-semibold">Bundle &amp; Save</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-display mb-4">
            Exclusive Bundle Packages
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Get more for less. Our curated bundles combine the best plans and add-ons at unbeatable prices.
          </p>
        </div>

        {/* Bundle Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {bundleDeals.map((bundle) => (
            <div
              key={bundle.id}
              className="relative bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 hover:-translate-y-1 transition-all group"
            >
              {/* Tag */}
              <div className="mb-6">
                <span className={`inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full border ${tagColors[bundle.tag] || 'bg-slate-700 text-slate-300 border-slate-600'}`}>
                  {bundle.tag}
                </span>
              </div>

              {/* Icon & Name */}
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-5">
                <i className={`${bundle.icon} text-2xl text-amber-400`}></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{bundle.name}</h3>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">{bundle.description}</p>

              {/* Includes */}
              <div className="mb-6">
                <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold mb-3">Includes</p>
                <ul className="space-y-2">
                  {bundle.includes.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <i className="ri-checkbox-circle-fill text-emerald-400 mt-0.5 w-4 h-4 flex items-center justify-center shrink-0"></i>
                      <span className="text-slate-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pricing */}
              <div className="bg-slate-900/50 rounded-xl p-4 mb-6">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-3xl font-bold text-white">${bundle.salePrice}</span>
                  <span className="text-slate-500 line-through">${bundle.originalPrice}</span>
                </div>
                <p className="text-emerald-400 text-sm font-semibold">
                  <i className="ri-arrow-down-line mr-1"></i>
                  Save ${bundle.savings} AUD
                </p>
              </div>

              {/* CTA */}
              <button
                onClick={() => handleClaim(bundle.id)}
                disabled={loadingId === bundle.id}
                className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-amber-500/20 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loadingId === bundle.id ? 'Processing...' : 'Get This Bundle'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
